import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || '/api';

export default function PromotionsTab({ settings, t }) {
  // ALL state owned locally
  const [promotions, setPromotions] = useState([]);
  const [showPromoModal, setShowPromoModal] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [promoForm, setPromoForm] = useState({ title_en: '', title_fr: '', body_en: '', body_fr: '', cta_text_en: '', cta_text_fr: '', cta_url: '', placement: 'home_hero', image_url: '', background_color: '', text_color: '', priority: 0, start_at: '', end_at: '', is_active: true });
  const [promoSaving, setPromoSaving] = useState(false);
  const [promoLangTab, setPromoLangTab] = useState('fr');
  const [promoTranslating, setPromoTranslating] = useState(false);

  // Load promotions on mount
  useEffect(() => {
    loadPromotions();
  }, []);

  const loadPromotions = async () => {
    try {
      const res = await axios.get(`${API}/admin/promotions`);
      setPromotions(res.data);
    } catch (err) { /* ignore */ }
  };

  const openPromoModal = (promo = null) => {
    setPromoLangTab('fr');
    if (promo) {
      setEditingPromo(promo);
      setPromoForm({
        title_en: promo.title_en || '', title_fr: promo.title_fr || '',
        body_en: promo.body_en || '', body_fr: promo.body_fr || '',
        cta_text_en: promo.cta_text_en || '', cta_text_fr: promo.cta_text_fr || '',
        cta_url: promo.cta_url || '', placement: promo.placement || 'home_hero',
        image_url: promo.image_url || '', background_color: promo.background_color || '',
        text_color: promo.text_color || '', priority: promo.priority || 0,
        start_at: promo.start_at ? promo.start_at.substring(0, 16) : '',
        end_at: promo.end_at ? promo.end_at.substring(0, 16) : '',
        is_active: promo.is_active
      });
    } else {
      setEditingPromo(null);
      setPromoForm({ title_en: '', title_fr: '', body_en: '', body_fr: '', cta_text_en: '', cta_text_fr: '', cta_url: '', placement: 'home_hero', image_url: '', background_color: '', text_color: '', priority: 0, start_at: '', end_at: '', is_active: true });
    }
    setShowPromoModal(true);
  };

  const autoTranslatePromo = async () => {
    setPromoTranslating(true);
    try {
      const source = promoLangTab;
      const target = source === 'fr' ? 'en' : 'fr';
      const fields = [
        { src: `title_${source}`, dst: `title_${target}` },
        { src: `body_${source}`, dst: `body_${target}` },
        { src: `cta_text_${source}`, dst: `cta_text_${target}` }
      ];
      const textsToTranslate = [];
      const fieldMap = [];
      for (let i = 0; i < fields.length; i++) {
        const val = promoForm[fields[i].src];
        if (val) {
          textsToTranslate.push(val);
          fieldMap.push(i);
        }
      }
      if (textsToTranslate.length > 0) {
        const res = await axios.post(`${API}/admin/translate`, { texts: textsToTranslate, source_lang: source, target_lang: target });
        const translated = res.data.translated;
        const updates = {};
        for (let j = 0; j < fieldMap.length; j++) {
          updates[fields[fieldMap[j]].dst] = translated[j];
        }
        setPromoForm(prev => ({ ...prev, ...updates }));
      }
    } catch (err) {
      alert(err.response?.data?.error || t.admin.promotions.translateError);
    } finally {
      setPromoTranslating(false);
    }
  };

  const savePromo = async () => {
    setPromoSaving(true);
    try {
      const payload = { ...promoForm, start_at: promoForm.start_at || null, end_at: promoForm.end_at || null };
      if (editingPromo) {
        await axios.put(`${API}/admin/promotions/${editingPromo.id}`, payload);
      } else {
        await axios.post(`${API}/admin/promotions`, payload);
      }
      setShowPromoModal(false);
      loadPromotions();
    } catch (err) {
      alert(t.admin.promotions.saveError);
    } finally {
      setPromoSaving(false);
    }
  };

  const togglePromoActive = async (promo) => {
    try {
      await axios.patch(`${API}/admin/promotions/${promo.id}/toggle`);
      loadPromotions();
    } catch (err) { /* ignore */ }
  };

  const deletePromo = async (promo) => {
    if (!window.confirm(t.admin.promotions.confirmDelete)) return;
    try {
      await axios.delete(`${API}/admin/promotions/${promo.id}`);
      loadPromotions();
    } catch (err) { /* ignore */ }
  };

  return (
    <>
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <h2 style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '2rem', color: '#D42B2B', margin: 0 }}>{t.admin.promotions.title}</h2>
          <button className="btn btn-primary" onClick={() => openPromoModal()}>
            {t.admin.promotions.addPromo}
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-dark table-hover">
            <thead>
            <tr>
              <th>{t.admin.services.status}</th>
              <th>{t.admin.promotions.placement}</th>
              <th>{t.admin.promotions.titleEn} / {t.admin.promotions.titleFr}</th>
              <th>{t.admin.promotions.startAt} / {t.admin.promotions.endAt}</th>
              <th>{t.admin.promotions.priority}</th>
              <th>{t.admin.promotions.views} / {t.admin.promotions.clicks}</th>
              <th>{t.admin.tableHeaders.actions}</th>
            </tr>
            </thead>
            <tbody>
            {promotions.map(promo => (
                <tr key={promo.id}>
                  <td>
                    <span className={`badge badge-status-${promo.is_active ? 'active' : 'inactive'}`} style={{ padding: '0.4rem 0.8rem', fontSize: '0.7rem' }}>
                      {promo.is_active ? t.admin.promotions.active : t.admin.promotions.inactive}
                    </span>
                  </td>
                  <td><span style={{ fontSize: '0.8rem' }}>{t.admin.promotions.placements?.[promo.placement] || promo.placement}</span></td>
                  <td>
                    <div style={{ color: '#E8ECF0', fontSize: '0.9rem' }}>{promo.title_en}{(!promo.title_fr?.trim() || !promo.title_en?.trim()) && <span className="badge bg-warning text-dark ms-1" style={{ fontSize: '0.6rem' }}>&#9888; Translation</span>}</div>
                    {promo.title_fr && <div style={{ color: '#94A3B8', fontSize: '0.75rem' }}>{promo.title_fr}</div>}
                  </td>
                  <td style={{ fontSize: '0.8rem' }}>
                    {promo.start_at || promo.end_at
                      ? `${promo.start_at ? new Date(promo.start_at).toLocaleDateString() : '...'} → ${promo.end_at ? new Date(promo.end_at).toLocaleDateString() : '...'}`
                      : t.admin.promotions.always}
                  </td>
                  <td style={{ color: '#D42B2B' }}>{promo.priority}</td>
                  <td style={{ fontSize: '0.8rem' }}>
                    <div>{promo.total_views} {t.admin.promotions.views} ({promo.views_today} {t.admin.promotions.today})</div>
                    <div>{promo.total_clicks} {t.admin.promotions.clicks} ({promo.clicks_today} {t.admin.promotions.today})</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: '0.3rem' }}>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => openPromoModal(promo)} style={{ fontSize: '0.7rem' }}>
                        {t.common.edit}
                      </button>
                      <button className="btn btn-outline-primary btn-sm" onClick={() => togglePromoActive(promo)} style={{ fontSize: '0.7rem' }}>
                        {promo.is_active ? t.admin.promotions.inactive : t.admin.promotions.active}
                      </button>
                      <button className="btn btn-outline-danger btn-sm" onClick={() => deletePromo(promo)} style={{ fontSize: '0.7rem' }}>
                        {t.common.delete}
                      </button>
                    </div>
                  </td>
                </tr>
            ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Promotion Modal */}
      {showPromoModal && (
          <div className="modal d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{editingPromo ? t.admin.promotions.editPromo : t.admin.promotions.addPromo}</h5>
                  <button type="button" className="btn-close" aria-label="Close" onClick={() => setShowPromoModal(false)}></button>
                </div>
                <div className="modal-body">
                  {/* FR/EN tab pills + auto-translate */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                    <ul className="nav nav-pills" style={{ gap: '0.5rem' }}>
                      <li className="nav-item">
                        <button className={`nav-link ${promoLangTab === 'fr' ? 'active' : ''}`} onClick={() => setPromoLangTab('fr')}>{t.admin.services.langTabFr}</button>
                      </li>
                      <li className="nav-item">
                        <button className={`nav-link ${promoLangTab === 'en' ? 'active' : ''}`} onClick={() => setPromoLangTab('en')}>{t.admin.services.langTabEn}</button>
                      </li>
                    </ul>
                    <button className="btn btn-outline-primary btn-sm" onClick={autoTranslatePromo} disabled={promoTranslating}>
                      {promoTranslating ? t.admin.services.translating : t.admin.services.autoTranslate}
                    </button>
                  </div>

                  {/* Language-specific fields */}
                  {promoLangTab === 'fr' && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.promotions.titleFr} *</label>
                        <input className="form-control" value={promoForm.title_fr} onChange={e => setPromoForm({ ...promoForm, title_fr: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.promotions.bodyFr}</label>
                        <textarea className="form-control" rows={2} value={promoForm.body_fr} onChange={e => setPromoForm({ ...promoForm, body_fr: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.promotions.ctaTextFr}</label>
                        <input className="form-control" value={promoForm.cta_text_fr} onChange={e => setPromoForm({ ...promoForm, cta_text_fr: e.target.value })} />
                      </div>
                    </>
                  )}
                  {promoLangTab === 'en' && (
                    <>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.promotions.titleEn} *</label>
                        <input className="form-control" value={promoForm.title_en} onChange={e => setPromoForm({ ...promoForm, title_en: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.promotions.bodyEn}</label>
                        <textarea className="form-control" rows={2} value={promoForm.body_en} onChange={e => setPromoForm({ ...promoForm, body_en: e.target.value })} />
                      </div>
                      <div className="mb-3">
                        <label className="form-label">{t.admin.promotions.ctaTextEn}</label>
                        <input className="form-control" value={promoForm.cta_text_en} onChange={e => setPromoForm({ ...promoForm, cta_text_en: e.target.value })} />
                      </div>
                    </>
                  )}

                  <div className="row mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label">{t.admin.promotions.ctaUrl}</label>
                      <input className="form-control" value={promoForm.cta_url} onChange={e => setPromoForm({ ...promoForm, cta_url: e.target.value })} placeholder="/book or https://..." />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">{t.admin.promotions.placement} *</label>
                      <select className="form-select" value={promoForm.placement} onChange={e => setPromoForm({ ...promoForm, placement: e.target.value })}>
                        {['home_hero', 'home_inline', 'services_top', 'booking_sidebar', 'sitewide_banner'].map(p => (
                          <option key={p} value={p}>{t.admin.promotions.placements?.[p] || p}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">{t.admin.promotions.imageUrl}</label>
                    <input className="form-control" value={promoForm.image_url} onChange={e => setPromoForm({ ...promoForm, image_url: e.target.value })} placeholder="https://..." />
                  </div>
                  <div className="row mb-3">
                    <div className="col-4">
                      <label className="form-label">{t.admin.promotions.bgColor}</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input type="color" value={promoForm.background_color || '#111827'} onChange={e => setPromoForm({ ...promoForm, background_color: e.target.value })} style={{ width: '40px', height: '34px', padding: 0, border: 'none' }} />
                        <input className="form-control form-control-sm" value={promoForm.background_color} onChange={e => setPromoForm({ ...promoForm, background_color: e.target.value })} placeholder="#111827" />
                      </div>
                    </div>
                    <div className="col-4">
                      <label className="form-label">{t.admin.promotions.txtColor}</label>
                      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                        <input type="color" value={promoForm.text_color || '#E8ECF0'} onChange={e => setPromoForm({ ...promoForm, text_color: e.target.value })} style={{ width: '40px', height: '34px', padding: 0, border: 'none' }} />
                        <input className="form-control form-control-sm" value={promoForm.text_color} onChange={e => setPromoForm({ ...promoForm, text_color: e.target.value })} placeholder="#E8ECF0" />
                      </div>
                    </div>
                    <div className="col-4">
                      <label className="form-label">{t.admin.promotions.priority}</label>
                      <input type="number" className="form-control" value={promoForm.priority} onChange={e => setPromoForm({ ...promoForm, priority: parseInt(e.target.value) || 0 })} />
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-12 col-md-6">
                      <label className="form-label">{t.admin.promotions.startAt}</label>
                      <input type="datetime-local" className="form-control" value={promoForm.start_at} onChange={e => setPromoForm({ ...promoForm, start_at: e.target.value })} style={{ colorScheme: 'dark' }} />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">{t.admin.promotions.endAt}</label>
                      <input type="datetime-local" className="form-control" value={promoForm.end_at} onChange={e => setPromoForm({ ...promoForm, end_at: e.target.value })} style={{ colorScheme: 'dark' }} />
                    </div>
                  </div>
                  <div className="form-check form-switch mb-3">
                    <input className="form-check-input" type="checkbox" id="promoActive" checked={promoForm.is_active} onChange={e => setPromoForm({ ...promoForm, is_active: e.target.checked })} />
                    <label className="form-check-label" htmlFor="promoActive">{t.admin.promotions.active}</label>
                  </div>

                  {/* Live Preview */}
                  <div style={{ borderTop: '1px solid rgba(148,163,184,0.15)', paddingTop: '1rem', marginTop: '1rem' }}>
                    <div style={{ fontSize: '0.7rem', letterSpacing: '0.1em', color: '#94A3B8', textTransform: 'uppercase', marginBottom: '0.8rem' }}>{t.admin.promotions.preview}</div>
                    <div style={{
                      background: promoForm.background_color || '#111827',
                      border: '1px solid rgba(148,163,184,0.2)',
                      padding: '1.2rem', borderRadius: '2px',
                    }}>
                      <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: '1.2rem', color: promoForm.text_color || '#E8ECF0', fontWeight: 500, marginBottom: '0.3rem' }}>
                        {promoForm.title_en || 'Title EN'} {promoForm.title_fr && <span style={{ color: '#94A3B8', fontSize: '0.85rem' }}>/ {promoForm.title_fr}</span>}
                      </div>
                      {promoForm.body_en && <div style={{ color: '#94A3B8', fontSize: '0.8rem', marginBottom: '0.5rem' }}>{promoForm.body_en}</div>}
                      {promoForm.cta_text_en && (
                        <span style={{ background: '#D42B2B', color: '#0C1220', padding: '0.3rem 0.8rem', fontSize: '0.6rem', letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 600 }}>
                          {promoForm.cta_text_en}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="modal-footer">
                  <button className="btn btn-outline-primary" onClick={() => setShowPromoModal(false)}>{t.common.cancel}</button>
                  <button className="btn btn-primary" onClick={savePromo} disabled={promoSaving || !promoForm.title_en?.trim() || !promoForm.title_fr?.trim() || !promoForm.placement}>
                    {promoSaving ? t.admin.promotions.saving : t.admin.promotions.savePromo}
                  </button>
                </div>
              </div>
            </div>
          </div>
      )}
    </>
  );
}
